import React, { useState, useEffect, useRef } from 'react';
import { IonContent, IonItem, IonLabel, IonModal, IonText } from '@ionic/react';
import { Helmet } from 'react-helmet';

// Note images mapping
const noteImages: Record<string, string> = {
    '₹1': '/assets/coins/1_rupee.png',
    '₹2': '/assets/coins/2_rupee.png',
    '₹5': '/assets/coins/5_rupee.png',
    '₹10': '/assets/coins/10_rupee.png',
    '₹20': '/assets/coins/20_rupee.png',
    '₹50': '/assets/coins/50_rupee.png',
    '₹100': '/assets/coins/100_rupee.png',
};

const generateRandomNoteQuestion = () => {
    const notes = Object.keys(noteImages);
    const randomNote = notes[Math.floor(Math.random() * notes.length)];
    return randomNote;
};

const IdentifyingMoney: React.FC = () => {
    const [currentNote, setCurrentNote] = useState<string>('');
    const [options, setOptions] = useState<string[]>([]);
    const [selectedAnswer, setSelectedAnswer] = useState<string>('');
    const [showImage, setShowImage] = useState(false);
    const [error, setError] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewQuestion();
        document.title = "Identifying Coins and Notes – UKG Money Concepts | Mama’s Guidance"; // Set page title
    }, []);

    const generateNewQuestion = () => {
        const newNote = generateRandomNoteQuestion();
        setCurrentNote(newNote);

        // Generate 3 wrong answers + 1 correct answer
        const allNotes = Object.keys(noteImages).filter(n => n !== newNote);
        const wrongAnswers = allNotes.sort(() => 0.5 - Math.random()).slice(0, 3);
        const newOptions = [...wrongAnswers, newNote].sort(() => 0.5 - Math.random());
        setOptions(newOptions);

        setSelectedAnswer('');
        setError(false);
    };

    const handleSubmit = (userAnswer: string) => {
        if (userAnswer === currentNote) {
            setShowImage(true);
            setTimeout(() => {
                generateNewQuestion();
                setShowImage(false);
            }, 5000);
        } else {
            setError(true);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach UKG children to identify Indian coins and notes through fun activities. Build early money recognition skills with printable worksheets and real-life examples." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/money/identifing" />
            </Helmet>
            {showImage === false ? (
                <div className='contentPart showCenter additionWord MoneyDiv'>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Identifying Coins and Notes</h1>

                            <p>
                                Teaching young children the basics of money is an essential life skill. In this <strong>“Identifying Coins and Notes”</strong> activity, UKG students will learn to recognize and name different Indian currency denominations, including ₹1, ₹2, ₹5 coins and ₹10, ₹20, ₹50 notes.
                            </p>

                            <h2>🪙 Why Learn About Money Early?</h2>
                            <ul>
                                <li>Builds real-life numeracy and recognition skills</li>
                                <li>Teaches the value of different denominations</li>
                                <li>Prepares children for simple transactions</li>
                                <li>Develops early math, observation, and memory</li>
                            </ul>

                            <h2>💰 What Your Child Will Learn</h2>
                            <ul>
                                <li>Identifying Indian coins (₹1, ₹2, ₹5, ₹10)</li>
                                <li>Identifying Indian currency notes (₹10, ₹20, ₹50, etc.)</li>
                                <li>Understanding the appearance, color, and size of each denomination</li>
                                <li>Linking value with number and visual patterns</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>💡 Real-Life Practice Ideas</h2>
                            <ul>
                                <li>Let your child sort real or toy coins by value</li>
                                <li>Play “shopkeeper” games with pretend money</li>
                                <li>Use grocery bills to point out currency denominations</li>
                                <li>Ask your child to guess the value of coins and notes you show them</li>
                            </ul>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Identifying Coins and Notes</h1>

                        <p>
                            Teaching young children the basics of money is an essential life skill. In this <strong>“Identifying Coins and Notes”</strong> activity, UKG students will learn to recognize and name different Indian currency denominations, including ₹1, ₹2, ₹5 coins and ₹10, ₹20, ₹50 notes.
                        </p>

                        <h2>🪙 Why Learn About Money Early?</h2>
                        <ul>
                            <li>Builds real-life numeracy and recognition skills</li>
                            <li>Teaches the value of different denominations</li>
                            <li>Prepares children for simple transactions</li>
                            <li>Develops early math, observation, and memory</li>
                        </ul>

                        <h2>💰 What Your Child Will Learn</h2>
                        <ul>
                            <li>Identifying Indian coins (₹1, ₹2, ₹5, ₹10)</li>
                            <li>Identifying Indian currency notes (₹10, ₹20, ₹50, etc.)</li>
                            <li>Understanding the appearance, color, and size of each denomination</li>
                            <li>Linking value with number and visual patterns</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/mathematics/smallest-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Smallest Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/biggest-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Biggest Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/number-comparison"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Number Comparison</IonLabel>
                            </IonItem>
                        </div>

                        <h2>💡 Real-Life Practice Ideas</h2>
                        <ul>
                            <li>Let your child sort real or toy coins by value</li>
                            <li>Play “shopkeeper” games with pretend money</li>
                            <li>Use grocery bills to point out currency denominations</li>
                            <li>Ask your child to guess the value of coins and notes you show them</li>
                        </ul>
                    </article>
                    <div style={{ padding: '20px' }}>
                        <IonText>
                            <p>Identify the value of this note:</p>
                        </IonText>
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            {currentNote && (
                                <img
                                    src={noteImages[currentNote]}
                                    alt={currentNote}
                                    style={{
                                        width: '150px',
                                        height: '100px',
                                        border: '1px solid #ccc',
                                        borderRadius: '5px',
                                        padding: '5px',
                                    }}
                                />
                            )}
                        </div>
                        <div className=' mb-4' style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    className={`background-blue ${selectedAnswer === option ? (option === currentNote ? 'correct' : 'incorrect') : ''}`}
                                    onClick={() => handleSubmit(option)}
                                    style={{ padding: '10px 20px', cursor: 'pointer', borderRadius: '5px' }}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} alt="Well Done" />
                </div>
            )}
        </>
    );
};

export default IdentifyingMoney;
